/**
 * Match system, Light or Dark, as the phone's More → Appearance offers.
 *
 * The choice is kept on this device under the key the web has always used,
 * which held `light` or `dark`; `system` is the new third value. The resolved
 * theme is what `data-theme` on the root carries, so a view that needs to know
 * it (the landscape's night sky) reads that attribute rather than asking the
 * media query itself.
 */

import { useEffect, useState, useSyncExternalStore } from 'react';

export type Appearance = 'system' | 'light' | 'dark';

const storageKey = 'cairn-theme';

const scheme = () => matchMedia('(prefers-color-scheme: dark)');

export function storedAppearance(): Appearance {
  try {
    const value = localStorage.getItem(storageKey);

    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

export function resolveAppearance(appearance: Appearance): 'light' | 'dark' {
  return appearance === 'system' ? (scheme().matches ? 'dark' : 'light') : appearance;
}

/** Applies a choice to the document, remembers it, and follows the system while matching it. */
export function useAppearance() {
  const [appearance, setAppearance] = useState(storedAppearance);

  useEffect(() => {
    const apply = () => {
      document.documentElement.dataset.theme = resolveAppearance(appearance);
    };

    apply();

    try {
      localStorage.setItem(storageKey, appearance);
    } catch {
      // A private window may refuse storage; the choice still holds for this visit.
    }

    if (appearance !== 'system') {
      return;
    }

    const query = scheme();

    query.addEventListener('change', apply);

    return () => query.removeEventListener('change', apply);
  }, [appearance]);

  return [appearance, setAppearance] as const;
}

function subscribeTheme(change: () => void) {
  const observer = new MutationObserver(change);

  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

  return () => observer.disconnect();
}

/** Whether the page is dark right now, following every change of appearance. */
export function useDark() {
  return useSyncExternalStore(
    subscribeTheme,
    () => document.documentElement.dataset.theme === 'dark',
    () => false,
  );
}

function subscribeMotion(change: () => void) {
  const query = matchMedia('(prefers-reduced-motion: reduce)');

  query.addEventListener('change', change);

  return () => query.removeEventListener('change', change);
}

/** Reduce Motion, as the phone honours it: every animation gives way to a fade or to nothing. */
export function useReducedMotion() {
  return useSyncExternalStore(
    subscribeMotion,
    () => matchMedia('(prefers-reduced-motion: reduce)').matches,
    () => false,
  );
}

/** The width an element is laid out at, for drawings that are plotted in pixels. */
export function useWidth<T extends Element>() {
  const [element, setElement] = useState<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!element) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));

    observer.observe(element);

    return () => observer.disconnect();
  }, [element]);

  return [setElement, width] as const;
}
